/**
 * Server-wait clock for journey timing (src/utils/journeyTiming.ts).
 *
 * The Supabase client's fetch is wrapped with `withServerActivityTiming`, so
 * every REST, RPC, Edge Function and Storage request tells the running
 * journeys when it starts and settles. Each journey turns that into
 * `server_ms`: wall time with at least one request in flight.
 *
 * This module has no imports on purpose: the Supabase client loads it, and
 * journeyTiming (which imports analytics, which imports the client) must not
 * create an import cycle. It only observes requests; it never changes the
 * request, the response or the promise the caller gets.
 */

export interface ServerActivityListener {
  onRequestStart(): void;
  onRequestEnd(): void;
}

const listeners = new Set<ServerActivityListener>();

const noop = () => {};

export function subscribeServerActivity(listener: ServerActivityListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Tells current listeners a request started. Returns the settle callback;
 * calling it more than once is harmless. Listeners that subscribe later never
 * see this request, so their in-flight counts stay balanced.
 */
export function noteServerRequestStart(): () => void {
  if (listeners.size === 0) return noop;
  const active = Array.from(listeners);
  for (const listener of active) {
    try {
      listener.onRequestStart();
    } catch {
      // Timing must never break a request.
    }
  }
  let settled = false;
  return () => {
    if (settled) return;
    settled = true;
    for (const listener of active) {
      try {
        listener.onRequestEnd();
      } catch {
        // ignore
      }
    }
  };
}

/** Wraps a fetch so running journeys can measure time spent waiting on it. */
export function withServerActivityTiming(baseFetch: typeof fetch): typeof fetch {
  return (input, init) => {
    const settle = noteServerRequestStart();
    let response: Promise<Response>;
    try {
      response = baseFetch(input, init);
    } catch (error) {
      settle();
      throw error;
    }
    // Observe only: the caller still receives the original promise.
    Promise.resolve(response).then(settle, settle);
    return response;
  };
}

/** Test-only. */
export function __resetServerActivityForTests(): void {
  listeners.clear();
}
